
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Terminal, Bot, ShieldCheck, Repeat } from 'lucide-react';

export const ApiSdks = () => (
  <div className="space-y-12">
    <section>
        <div className="flex items-start gap-4">
            <div className="p-3 bg-stone-100 dark:bg-white/10 rounded-xl text-stone-900 dark:text-white">
                <Terminal size={24} />
            </div>
            <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium pt-1">
                GhostPay provides a robust set of SDKs for developers. Whether you are creating trustless escrow contracts or deploying autonomous AI agents, our libraries simplify the complexity of on-chain interactions.
            </p>
        </div>
    </section>

    {/* Escrow SDK */}
    <section>
        <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-4">Escrow & Payments SDK</h3>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 max-w-2xl">
            The primary SDK for creating and managing on-chain financial agreements. This example demonstrates deploying a milestone-based escrow contract with an optional AI agent for validation.
        </p>
        <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-stone-200 to-stone-300 dark:from-stone-800 dark:to-stone-700 rounded-xl blur opacity-25"></div>
            <pre className="relative bg-[#0A0B0F] text-stone-300 p-6 rounded-xl text-sm font-mono overflow-x-auto border border-white/10 shadow-xl">
<code className="language-js">{`import { GhostPay } from '@ghostpay/sdk';

const ghostpay = new GhostPay({ apiKey: 'YOUR_GHOST_SECRET_KEY' });

// Create a new escrow contract with an AI agent
async function createSmartEscrow() {
  const escrow = await ghostpay.escrows.create({
    buyer: 'BUYER_PUBKEY',
    seller: 'SELLER_PUBKEY',
    token: 'USDC',
    milestones: [
      { amount: 500, description: 'Initial Deposit' },
      { amount: 1000, description: 'Final Delivery' }
    ],
    stealth: true, // Use a one-time address for privacy
    aiAgent: 'DEFAULT_AI_AGENT_PUBKEY' // Optional: Assign an agent for validation
  });

  console.log('Escrow Contract Deployed:', escrow.contractAddress);
}

createSmartEscrow();`}</code>
            </pre>
        </div>
    </section>

    {/* AI Agent SDK */}
    <section>
        <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-2">
            <Bot size={18} className="text-nobel-gold"/> AI Agent SDK
        </h3>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-6 max-w-2xl">
            The AI Agent SDK enables autonomous validation and execution of on-chain events. Agents can act as impartial arbiters for escrow releases based on off-chain data (e.g., shipping APIs, oracle data).
        </p>
        
        <div className="relative group">
            <pre className="relative bg-[#0A0B0F] text-stone-300 p-6 rounded-xl text-sm font-mono overflow-x-auto border border-white/10 shadow-xl">
<code className="language-js">{`import { GhostAgent } from '@ghostpay/sdk';

// AI Agent listening for off-chain events
const agent = new GhostAgent({ wallet: process.env.AGENT_WALLET_KEY });

// Agent validates and signs a milestone release
async function releaseMilestoneWithAI(escrowId, milestoneIndex) {
  // 1. Agent performs off-chain validation (e.g., checks a shipping API)
  const isComplete = await checkShippingStatus(escrowId, milestoneIndex);
  
  if (!isComplete) {
    throw new Error('AI Validation Failed: Milestone conditions not met.');
  }

  // 2. If valid, agent signs and releases the milestone funds
  const tx = await agent.escrows.releaseMilestone({
    escrowId: escrowId,
    milestoneIndex: milestoneIndex,
  });

  console.log('Milestone Released by AI:', tx.signature);
}

// Example trigger (could be a webhook or scheduled job)
releaseMilestoneWithAI('ESCROW_CONTRACT_ADDRESS', 1);`}</code>
            </pre>
        </div>
    </section>
    
    <p className="text-stone-600 dark:text-stone-400 text-center pt-8 border-t border-stone-200 dark:border-white/10">
        For full documentation, please visit the <span className="text-nobel-gold font-bold">API Reference</span> section.
    </p>
  </div>
);
